import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import toast from "../../utils/toast";
import {
  upsertPathologyDraft,
  verifyPathologyReport,
  updatePathologistReport,
} from "../../store/slices/pathologistResultSlice";
import { fetchTestOrderById } from "../../store/slices/testOrderSlice";
import uploadService from "../../services/uploadService";

const safeJsonParse = (v) => {
  if (!v) return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const DEFAULT_REPORT = {
  clinicalHistory: "",
  specimen: "",
  gross: "",
  microscopy: "",
  impression: "",
  diagnosis: "",
  checklist: [""],
};

const normalizeChecklist = (v) => {
  if (!Array.isArray(v)) return [""];
  const cleaned = v.map((x) => (x == null ? "" : String(x)));
  return cleaned.length ? cleaned : [""];
};

const MorphologyReportEntry = () => {
  const { orderId } = useParams();
  const testOrderId = Number(orderId);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { currentReport, loading, error } = useSelector((s) => s.pathologistReports || {});
  const { currentTestOrder } = useSelector((s) => s.testOrders || {});
  const { token } = useSelector((s) => s.auth || {});

  const [form, setForm] = useState(DEFAULT_REPORT);
  // Support multiple images per morphology report (array of URLs)
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const isLocked = useMemo(() => {
    const status = currentReport?.status;
    const isVerified = Number(currentReport?.is_verified || 0) === 1;
    return isVerified || status === "verified" || status === "final";
  }, [currentReport?.is_verified, currentReport?.status]);

  useEffect(() => {
    if (!Number.isFinite(testOrderId)) return;
    dispatch(fetchTestOrderById(testOrderId));
    dispatch(upsertPathologyDraft({ test_order_id: testOrderId }));
  }, [dispatch, testOrderId]);

  useEffect(() => {
    if (!currentReport) return;

    const parsed = safeJsonParse(currentReport.report_data);
    const merged = {
      ...DEFAULT_REPORT,
      ...(parsed && typeof parsed === "object" ? parsed : {}),
    };
    merged.checklist = normalizeChecklist(merged.checklist);

    setForm(merged);

    // Initialize local images array from current report (handles stringified JSON or array)
    const imagesParsed = safeJsonParse(currentReport.images);
    if (Array.isArray(imagesParsed)) {
      setImages(imagesParsed.map((u) => String(u)));
    } else if (typeof currentReport.images === "string" && currentReport.images.trim()) {
      try {
        const asJson = JSON.parse(currentReport.images);
        if (Array.isArray(asJson)) {
          setImages(asJson.map((u) => String(u)));
        } else {
          setImages([String(currentReport.images)]);
        }
      } catch {
        setImages([String(currentReport.images)]);
      }
    } else {
      setImages([]);
    }
  }, [currentReport]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setChecklistAt = (idx, value) => {
    setForm((prev) => {
      const next = [...normalizeChecklist(prev.checklist)];
      next[idx] = value;
      return { ...prev, checklist: next };
    });
  };

  const addChecklist = () => {
    setForm((prev) => ({ ...prev, checklist: [...normalizeChecklist(prev.checklist), ""] }));
  };

  const removeChecklist = (idx) => {
    setForm((prev) => {
      const next = normalizeChecklist(prev.checklist).filter((_, i) => i !== idx);
      return { ...prev, checklist: next.length ? next : [""] };
    });
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      // Validate file
      if (!file) {
        throw new Error("No file selected");
      }
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error("File size exceeds 10MB limit");
      }

      // Check file type
      if (!file.type.startsWith("image/")) {
        throw new Error("File must be an image");
      }

      // Get Cloudinary signature
      const sigResp = await uploadService.cloudinarySignature(
        { folder: "lis/pathologist" },
        token
      );
      
      // Handle different response structures
      const sig = sigResp?.data?.data ?? sigResp?.data ?? sigResp;
      const { cloudName, apiKey, timestamp, signature, folder } = sig || {};

      if (!cloudName || !apiKey || !timestamp || !signature) {
        console.error("[MorphologyReportEntry] Cloudinary signature response:", sig);
        throw new Error("Cloudinary signature response is incomplete. Please check backend configuration.");
      }

      // Upload to Cloudinary
      const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
      const body = new FormData();
      body.append("file", file);
      body.append("api_key", apiKey);
      body.append("timestamp", String(timestamp));
      body.append("signature", signature);
      if (folder) body.append("folder", folder);

      const resp = await fetch(url, {
        method: "POST",
        body,
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        let errorJson;
        try {
          errorJson = JSON.parse(errorText);
        } catch {
          throw new Error(`Cloudinary upload failed: ${resp.status} ${resp.statusText}`);
        }
        const message = errorJson?.error?.message || errorJson?.message || "Cloudinary upload failed";
        throw new Error(message);
      }

      const json = await resp.json();
      if (!json?.secure_url) {
        console.error("[MorphologyReportEntry] Cloudinary response:", json);
        throw new Error("Cloudinary did not return secure_url");
      }

      const imageUrl = String(json.secure_url);
      console.log("[MorphologyReportEntry] Image uploaded successfully, URL:", imageUrl);
      console.log("[MorphologyReportEntry] Full Cloudinary response:", json);
      
      // Add to local images array
      setImages((prev) => [...prev, imageUrl]);
      // Auto-save the image URLs to the draft (but don't wait for it to complete)
      if (currentReport?.id) {
        // Fire and forget - don't await to avoid blocking UI
        dispatch(updatePathologistReport({
          id: currentReport.id,
          data: {
            test_order_id: testOrderId,
            images: [...images, imageUrl],
            status: currentReport.status || "draft",
          }
        })).unwrap().then(() => {
          console.log("[MorphologyReportEntry] Image auto-saved to report");
        }).catch((saveError) => {
          console.warn("[MorphologyReportEntry] Failed to auto-save image:", saveError);
          // Don't throw - the image is uploaded and displayed, user can save manually
        });
      }
      
      toast.success("Image uploaded successfully!");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to upload image";
      console.error("[MorphologyReportEntry] Upload error:", e);
      toast.error(String(msg));
      throw e;
    } finally {
      setUploading(false);
    }
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      for (const file of files) {
        // Upload sequentially to reuse uploadImage validation + error handling
        // (images state is updated incrementally)
        // eslint-disable-next-line no-await-in-loop
        await uploadImage(file);
      }
    } finally {
      // Allow picking the same file again if needed
      e.target.value = "";
    }
  };

  const saveDraft = async () => {
    if (!currentReport?.id) throw new Error("No report loaded");

    // Ensure images is always an array, even if empty
    const imagesArray = Array.isArray(images) ? images : [];

    const data = {
      test_order_id: testOrderId,
      report_data: {
        clinicalHistory: form.clinicalHistory,
        specimen: form.specimen,
        gross: form.gross,
        microscopy: form.microscopy,
        impression: form.impression,
        diagnosis: form.diagnosis,
        checklist: normalizeChecklist(form.checklist).filter(
          (x) => String(x).trim().length > 0
        ),
      },
      images: imagesArray,
      status: currentReport.status || "draft",
    };

    console.log("[MorphologyReportEntry] Saving draft with images:", imagesArray);
    const result = await dispatch(updatePathologistReport({ id: currentReport.id, data })).unwrap();
    console.log("[MorphologyReportEntry] Draft saved, result:", result);
    return result;
  };

  const onSave = async () => {
    try {
      await saveDraft();
      toast.success("Draft saved.");
    } catch (e) {
      toast.error(String(e?.message || e || "Failed to save"));
    }
  };

  const onVerify = async () => {
    try {
      const diagnosisOk = String(form.diagnosis || "").trim().length > 0;
      if (!diagnosisOk) {
        toast.error("Diagnosis is required before verification.");
        return;
      }

      await saveDraft();
      await dispatch(verifyPathologyReport({ id: currentReport.id })).unwrap();
      toast.success("Verified & signed.");
    } catch (e) {
      toast.error(String(e?.message || e || "Failed to verify"));
    }
  };

  const onPrint = () => {
    if (!testOrderId) return;
    window.open(`/print-report?orderId=${testOrderId}`, "_blank", "noopener,noreferrer");
  };

  const order = useMemo(() => {
    const raw = currentTestOrder?.data ?? currentTestOrder ?? null;
    if (!raw) return null;
    if (testOrderId && String(raw.id) !== String(testOrderId)) return null;
    return raw;
  }, [currentTestOrder, testOrderId]);

  const testName = order?.test_name || order?.testName || "";
  const patientName = order?.patient_name || order?.patientName || "";

  const inputBase =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-transparent focus:ring-2 focus:ring-[#235F72] disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none disabled:cursor-not-allowed";
  const labelBase = "block text-sm font-semibold text-gray-800";
  const helpBase = "mt-1 text-xs text-gray-500";

  const primaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a4a5a] focus:outline-none focus:ring-2 focus:ring-[#235F72] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const secondaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#235F72] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const successBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const dangerLink = "text-sm font-semibold text-red-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed";
  const linkBtn = "text-sm font-semibold text-[#235F72] hover:underline disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="mx-auto w-full max-w-5xl p-4 sm:p-6">
      <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 flex flex-col h-[calc(100vh-120px)] min-h-[520px]">
        <div className="sticky top-0 z-10 border-b border-gray-200/70 bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#235F72]/10 px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#235F72]/20">
                Morphology
              </div>
              <h2 className="mt-2 text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                Morphology Report
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Order #{testOrderId}
                {patientName ? ` • ${patientName}` : ""}
                {testName ? ` • ${testName}` : ""}
              </p>
              {currentReport?.status && (
                <div className="mt-1 text-xs text-gray-500">Status: {String(currentReport.status)}</div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button className={secondaryBtn} onClick={() => navigate(-1)}>
                Back
              </button>
              <button className={secondaryBtn} onClick={onPrint} disabled={!currentReport}>
                Print preview
              </button>
              <button className={successBtn} onClick={onSave} disabled={loading || isLocked}>
                {loading ? "Saving…" : "Save draft"}
              </button>
              <button className={primaryBtn} onClick={onVerify} disabled={loading || isLocked}>
                Verify &amp; Sign
              </button>
            </div>
          </div>
        </div>

        {isLocked && (
          <div className="border-b border-gray-200/70 bg-red-50 px-5 py-4 sm:px-6">
            <div className="text-sm font-semibold text-red-800">Verified / Locked</div>
            <div className="mt-1 text-xs text-red-700">Once verified, editing is locked.</div>
          </div>
        )}

        {error && (
          <div className="border-b border-gray-200/70 bg-red-50 px-5 py-4 text-sm text-red-800 sm:px-6">
            {String(error)}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-6 overscroll-contain">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Form Inputs */}
            <div className="lg:col-span-2 space-y-6">
              <section className="rounded-2xl bg-white ring-1 ring-gray-200/70">
                <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
                  <div className="text-sm font-semibold text-gray-900">Clinical Details</div>
                  <div className="text-xs text-gray-500">History, specimen, and gross description.</div>
                </div>
                <div className="space-y-4 p-4 sm:p-5">
                  <div>
                    <label className={labelBase}>Clinical history</label>
                    <p className={helpBase}>Relevant clinical summary and indication.</p>
                    <textarea
                      className={`${inputBase} mt-2 resize-y`}
                      rows={3}
                      value={form.clinicalHistory}
                      onChange={(e) => setField("clinicalHistory", e.target.value)}
                      disabled={isLocked}
                    />
                  </div>

                  <div>
                    <label className={labelBase}>Specimen</label>
                    <p className={helpBase}>Specimen type, site, and quantity.</p>
                    <textarea
                      className={`${inputBase} mt-2 resize-y`}
                      rows={2}
                      value={form.specimen}
                      onChange={(e) => setField("specimen", e.target.value)}
                      disabled={isLocked}
                    />
                  </div>

                  <div>
                    <label className={labelBase}>Gross description</label>
                    <p className={helpBase}>Macroscopic findings.</p>
                    <textarea
                      className={`${inputBase} mt-2 resize-y`}
                      rows={4}
                      value={form.gross}
                      onChange={(e) => setField("gross", e.target.value)}
                      disabled={isLocked}
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-2xl bg-white ring-1 ring-gray-200/70">
                <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
                  <div className="text-sm font-semibold text-gray-900">Microscopy &amp; Interpretation</div>
                  <div className="text-xs text-gray-500">Key microscopic findings, impression, and diagnosis.</div>
                </div>
                <div className="space-y-4 p-4 sm:p-5">
                  <div>
                    <label className={labelBase}>Microscopy</label>
                    <p className={helpBase}>Describe morphology and relevant patterns.</p>
                    <textarea
                      className={`${inputBase} mt-2 resize-y`}
                      rows={6}
                      value={form.microscopy}
                      onChange={(e) => setField("microscopy", e.target.value)}
                      disabled={isLocked}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelBase}>Impression</label>
                      <p className={helpBase}>Concise summary of findings.</p>
                      <textarea
                        className={`${inputBase} mt-2 resize-y`}
                        rows={4}
                        value={form.impression}
                        onChange={(e) => setField("impression", e.target.value)}
                        disabled={isLocked}
                      />
                    </div>
                    <div>
                      <label className={labelBase}>
                        Diagnosis <span className="text-red-600">*</span>
                      </label>
                      <p className={helpBase}>Required for verification.</p>
                      <textarea
                        className={`${inputBase} mt-2 resize-y`}
                        rows={4}
                        value={form.diagnosis}
                        onChange={(e) => setField("diagnosis", e.target.value)}
                        disabled={isLocked}
                      />
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl bg-white ring-1 ring-gray-200/70">
                <div className="flex flex-col gap-2 border-b border-gray-200/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Checklist</div>
                    <div className="text-xs text-gray-500">Optional supporting points.</div>
                  </div>
                  <button className={linkBtn} onClick={addChecklist} disabled={isLocked}>
                    Add
                  </button>
                </div>
                <div className="space-y-3 p-4 sm:p-5">
                  {normalizeChecklist(form.checklist).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        className={inputBase}
                        value={item}
                        onChange={(e) => setChecklistAt(idx, e.target.value)}
                        disabled={isLocked}
                        placeholder="Checklist item"
                      />
                      <button className={dangerLink} onClick={() => removeChecklist(idx)} disabled={isLocked}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <div className="text-xs text-gray-500">Once verified, editing is locked.</div>
            </div>

            {/* Right Column - Image Section */}
            <div className="lg:col-span-1">
            <section className="rounded-2xl bg-white ring-1 ring-gray-200/70 sticky top-6">
                <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
                  <div className="text-sm font-semibold text-gray-900">Images</div>
                  <div className="text-xs text-gray-500">Upload one or more morphology images (Cloudinary).</div>
                </div>

                <div className="p-4 sm:p-5">
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={onPickFiles}
                      disabled={isLocked || uploading}
                      className="sr-only"
                      id="morph-upload"
                    />
                    <label
                      htmlFor="morph-upload"
                      className={`block cursor-pointer rounded-xl bg-white px-4 py-3 text-center shadow-sm ring-1 ring-gray-200/70 transition hover:bg-gray-50 focus-within:ring-2 focus-within:ring-[#235F72] ${
                        isLocked || uploading ? "cursor-not-allowed opacity-60" : ""
                      }`}
                    >
                      <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#235F72]/10 text-[#235F72]">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div className="text-xs font-semibold text-gray-900">
                        {uploading ? "Uploading…" : "Upload images"}
                      </div>
                      <div className="mt-1 text-[10px] text-gray-500">PNG/JPG up to 10MB</div>
                    </label>

                    {images && images.length > 0 && (
                      <div className="mt-4">
                        <div className="text-xs font-semibold text-gray-700 mb-2">
                          Preview
                          <span className="ml-2 text-[10px] font-normal text-gray-500">
                            ({images.length} image{images.length > 1 ? "s" : ""})
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                          {images.map((url, idx) => (
                            <div
                              key={url + idx}
                              className="relative overflow-hidden rounded-xl ring-1 ring-gray-200/70 bg-gray-100 aspect-square flex items-center justify-center"
                            >
                              <img
                                src={url}
                                alt={`Morphology ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                              {!isLocked && (
                                <button
                                  type="button"
                                  className="absolute top-1 right-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-red-700 shadow-sm"
                                  onClick={async () => {
                                    const next = images.filter((_, i) => i !== idx);
                                    setImages(next);
                                    if (currentReport?.id) {
                                      try {
                                        await dispatch(updatePathologistReport({
                                          id: currentReport.id,
                                          data: {
                                            test_order_id: testOrderId,
                                            images: next,
                                            status: currentReport.status || "draft",
                                          }
                                        })).unwrap();
                                      } catch (err) {
                                        console.warn("[MorphologyReportEntry] Failed to remove image from report:", err);
                                      }
                                    }
                                  }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MorphologyReportEntry;
